// src/pages/UserGuidePage.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const guidePdfUrl = "/assets/Hovastore%20User%20Guides.pdf";

const UserGuidePage: React.FC = () => {
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const { t } = useTranslation();
  const guideItems = [
    t("guide.itemStores"),
    t("guide.itemSales"),
    t("guide.itemExpenses"),
    t("guide.itemConfidence"),
  ];

  return (
    <section className="relative overflow-hidden bg-slate-50 px-4 py-20 dark:bg-slate-950 sm:px-6">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      <div className="absolute right-8 top-16 hidden h-24 w-24 rounded-full border border-primary/10 bg-primary/5 hs-soft-pulse lg:block" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="text-slate-950 dark:text-white">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary dark:text-pink-200">
            {t("guide.eyebrow")}
          </p>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-5xl">
            {t("guide.title")}
          </h1>
          <p className="mt-5 text-lg leading-8 text-slate-600 dark:text-slate-300">
            {t("guide.description")}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setIsGuideOpen(true)}
              className="inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 text-base font-bold text-white shadow-lg shadow-primary/20 transition hover:-translate-y-0.5 hover:bg-[#6d1f55]"
            >
              <svg
                className="-ml-1 mr-3 h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M4 3a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2H6a2 2 0 01-2-2V3z" />
              </svg>
              {t("guide.viewFull")}
            </button>
            <a
              href="#faqs"
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-6 py-3 text-base font-bold text-slate-800 transition hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {t("guide.commonQuestions")}
            </a>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-r from-primary/10 via-pink-300/10 to-emerald-300/10 blur-2xl" />
          <div className="relative rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-2xl shadow-slate-900/10 dark:border-slate-800 dark:bg-slate-900">
            <div className="grid gap-6 md:grid-cols-[0.85fr_1.15fr]">
              <div className="rounded-[1.35rem] border border-slate-200 bg-slate-50 p-5 hs-float-delay dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-primary dark:text-pink-200">
                    {t("guide.pdfGuide")}
                  </div>
                  <div className="rounded-full bg-white px-3 py-1 text-xs font-black text-primary shadow-sm dark:bg-slate-900">
                    PDF
                  </div>
                </div>
                <div className="mt-6 rounded-2xl bg-white p-5 shadow-xl shadow-slate-900/5 dark:bg-slate-900">
                  <div className="h-3 w-24 rounded-full bg-primary/20" />
                  <div className="mt-6 space-y-3">
                    <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700" />
                    <div className="h-2 w-5/6 rounded-full bg-slate-200 dark:bg-slate-700" />
                    <div className="h-2 w-2/3 rounded-full bg-slate-200 dark:bg-slate-700" />
                    <div className="h-2 w-4/5 rounded-full bg-slate-200 dark:bg-slate-700" />
                  </div>
                  <div className="mt-8 grid grid-cols-2 gap-3">
                    <div className="h-20 rounded-xl bg-slate-100 dark:bg-slate-800" />
                    <div className="h-20 rounded-xl bg-primary/10" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <h2 className="text-2xl font-black text-slate-950 dark:text-white">
                  {t("guide.workflowTitle")}
                </h2>
                <p className="mt-3 text-base leading-7 text-slate-600 dark:text-slate-300">
                  {t("guide.workflowDescription")}
                </p>
                <div className="mt-6 grid gap-3">
                  {guideItems.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 text-sm font-bold text-slate-800 dark:text-slate-100"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300">
                        ✓
                      </span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setIsGuideOpen(true)}
                  className="mt-8 rounded-xl border border-primary/20 bg-primary/10 px-6 py-3 text-base font-black text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-white dark:border-pink-300/20 dark:bg-pink-300/10 dark:text-pink-100"
                >
                  {t("guide.openPreview")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isGuideOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="guide-modal-title"
        >
          <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-950">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <div>
                <h3
                  id="guide-modal-title"
                  className="text-lg font-black text-slate-950 dark:text-white"
                >
                  {t("guide.modalTitle")}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t("guide.modalSubtitle")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsGuideOpen(false)}
                className="rounded-full border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                aria-label="Close user guide"
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <iframe
              title="HovaStore User Guides PDF"
              src={guidePdfUrl}
              className="h-[76vh] w-full bg-slate-100 dark:bg-slate-900"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default UserGuidePage;
